'use client'
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import styles from './ProjectModal.module.css';
import FsLightbox from "fslightbox-react";
import {getLinkImagePath, getProjectMediaPath, Project, ProjectModalProps} from "@/shared/pages/projectsShared";
import ExportedImage from "next-image-export-optimizer";
import {useRouter, useSearchParams} from "next/navigation";
import {consts} from "@/shared/commonShared";
import playIcon from '@/public/assets/imgs/new/icons/projects/play.svg';
// Helper to get the correct media path


export default function ProjectModal({ project, onClose, onCloseAction }: ProjectModalProps) {
    const router = useRouter();

    // state to track if the Lightbox is currently open
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    const [lightboxController, setLightboxController] = useState({
        toggler: false,
        slide: 1 // FsLightbox is 1-based, not 0-based
    });

    // Guard to ignore clicks that happen right after closing the lightbox
    const lastClosedAt = useRef(0);

    const searchParams = useSearchParams();
    const handleClose = useCallback(() => {
        if (onCloseAction) {
            const currentParams = searchParams.toString();
            const targetUrl = currentParams
                ? `${onCloseAction}?${currentParams}`
                : onCloseAction;
            router.push(targetUrl); // Navigate back to '/projects'
        } else if (onClose) {
            onClose();
        }
    },[onCloseAction, router, onClose]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (!isLightboxOpen) {
                    handleClose();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLightboxOpen, handleClose]);

    useEffect(() => {
        if (!project) return;

        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [project]);



    const openLightbox = useCallback(
        (index: number) => {
            if (Date.now() - lastClosedAt.current < 300) return;

            setLightboxController((prev) => ({
                toggler: !prev.toggler,
                slide: index + 1,
            }));
        },
        []
    );


    const galleryItems = useMemo(() => {
        if (!project) return [] as Project["media"];

        const items: Project["media"] = [];

        if (project.thumbnailUrl) {
            items.push({
                type: "image",
                filename: project.thumbnailUrl,
                youtubeId: null,
            });
        }

        project.media.forEach((item) => {
            if (!item.filename || item.filename !== project.thumbnailUrl) {
                items.push(item);
            }
        });

        return items;
    }, [project]);

    const lightboxSources = useMemo(
        () =>
            galleryItems.map((item, index) => {
                // YouTube video – let FsLightbox handle it as a video/iframe
                if (item.type === "video" && item.youtubeId) {
                    return `https://www.youtube.com/watch?v=${item.youtubeId}`;
                }

                const raw = getProjectMediaPath(item);

                // Self-hosted video → don't run through Next image optimizer
                if (item.type === "video") {
                    return raw;
                }

                // IMAGE: Return the ExportedImage Component
                // FsLightbox will render this as a custom slide
                return (
                    <div
                        key={index}
                        style={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                        }}
                    >
                        <ExportedImage
                            src={raw}
                            alt={project?.name || ""}
                            style={{
                                objectFit: 'contain',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                height: 'auto'
                            }}
                            width={1920}
                            height={1080}
                            priority={true}
                        />
                    </div>
                );
            }),
        [galleryItems, project?.name]
    );

    // Don't render if not mounted or no project
    if (!project) {
        return null;
    }

    // Create the modal JSX
    return (
        <div className={styles.projectModalOverlay+" p-0 p-md-5"} onClick={handleClose}>
            <div className={styles.projectModalContent+" bg-3 w-100 w-lg-auto p-"+consts.PADDING+" p-lg-"+consts.PADDING} onClick={(e) => e.stopPropagation()}>
                <button className={styles.projectModalClose+" bg-3 rounded position-fixed position-md-absolute"} onClick={handleClose}>&times;</button>

                <h2 className="text-2xl font-bold">{project.name}</h2>
                {project.company && <a target="_blank" href={project.companyLink || "#"} className="cursor-pointer text-700 mb-2">by {project.company}</a>}

                {galleryItems.length > 0 && (
                    <div className={styles.projectModalGallery}>
                        {galleryItems.map((item, index) => (
                            // 5. Add onClick handler to each gallery item
                            <div
                                className={styles.galleryItem}
                                key={index}
                                onClick={() => openLightbox(index)}
                            >
                                {item.type === "image" && item.filename && (
                                    <ExportedImage
                                        src={getProjectMediaPath(item)}
                                        alt={project.name}
                                        className={styles.galleryImage}
                                        width={400}
                                        height={225} // 16:9
                                        sizes="(max-width: 768px) 100vw, 25vw"
                                    />
                                )}
                                {item.type === 'video' && item.filename && (
                                    // Using a poster for self-hosted videos
                                    <video preload="metadata" style={{pointerEvents: 'none'}}>
                                        <source src={`${getProjectMediaPath(item)}#t=0.1`} type="video/mp4" />
                                    </video>
                                )}
                                {item.type === "video" && item.youtubeId && (
                                    <div className="position-relative w-100 h-100">
                                        <ExportedImage
                                            unoptimized={true}
                                            src={`https://img.youtube.com/vi/${item.youtubeId}/hqdefault.jpg`}
                                            alt="YouTube Video"
                                            className={styles.galleryImage}
                                            width={400}
                                            height={225}
                                            sizes="(max-width: 768px) 100vw, 25vw"
                                        />
                                        {/* Play Icon Overlay */}
                                        <div className="position-absolute top-50 start-50 translate-middle z-2" style={{pointerEvents: 'none'}}>
                                            <ExportedImage src={playIcon} alt="Play" width={50} height={50} style={{ width: '50px', height: '50px' }}/>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* ... (About, Responsibilities, Tech-stack, Links, etc. remain the same) ... */}

                <h6 className="text-lg font-semibold mt-4 mb-2">About:</h6>
                <p className="text-600 mb-4" style={{ whiteSpace: 'pre-wrap' }}>
                    {project.about}
                </p>

                {project.responsibilities && (
                    <>
                        <h6 className="text-lg font-semibold mb-2">Responsibilities:</h6>
                        <p className="text-600 mb-4" style={{ whiteSpace: 'pre-wrap' }}>
                            {project.responsibilities}
                        </p>
                    </>
                )}

                <h6 className="text-lg font-semibold mb-2">Tech-stack:</h6>
                <div className="d-flex flex-wrap gap-2 mb-4">
                    {project.techStacks.map((tech) => (
                        <span key={tech} className="badge bg-secondary text-white text-xs font-medium px-2 py-1 rounded">
                            {tech}
                        </span>
                    ))}
                </div>

                {project.links && project.links.length > 0 && (
                    <>
                        <h6 className="text-lg font-semibold mb-2">Links:</h6>
                        <div className={styles.projectModalLinksGrid}>
                            {project.links.map((link) => (
                                <a key={link.title} href={link.url} target="_blank" rel="noopener noreferrer" className={styles.linkCard+" d-flex flex-column gap-2 align-items-center justify-content-center bg-2"}>
                                    {link.type && <ExportedImage
                                        src={getLinkImagePath(link.type)}
                                        alt={link.title}
                                        width={64}
                                        height={64}
                                    />}
                                    <span>{link.title}</span>
                                </a>
                            ))}
                        </div>
                    </>
                )}

                {project.projectLink && (
                    <a href={project.projectLink} target="_blank" rel="noopener noreferrer" className="btn btn-gradient mt-4">
                        Visit Project
                    </a>
                )}

                {/* 6. Add the Lightbox component. It will be invisible until toggled. */}
                <FsLightbox
                    toggler={lightboxController.toggler}
                    sources={lightboxSources}
                    slide={lightboxController.slide}
                    onOpen={() => setIsLightboxOpen(true)}
                    onClose={() => { setIsLightboxOpen(false); lastClosedAt.current = Date.now(); }}
                />
            </div>
        </div>
    );
}